/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { MultiFileInputProps } from '../../../types/InputComponentProps';
import { InputContainer } from './FormInputContainer';
import { HStack, IconButton, VStack } from 'chakra-v3';
import { Button, Text } from '../../../../../components/Index';
import { RiCloseLine } from 'react-icons/ri';
import { useRef } from 'react';

export function MultiFileInput(props: MultiFileInputProps) {
  const {
    id,
    description,
    label,
    value,
    onChange,
    showFileNames,
    error,
    isInvalid,
    isDisabled,
    isRequired,
    acceptedTypes,
    size: _size,
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBrowse = () => {
    inputRef.current?.click();
  };

  const handleInputChange = () => {
    const uploadedFiles = inputRef.current?.files;
    const uploadedFilesArray = uploadedFiles ? Array.from(uploadedFiles) : undefined;
    if (uploadedFilesArray) {
      const files = [...value, ...uploadedFilesArray];
      onChange(id!, files);
    }
  };
  const handleDelete = (file: File) => {
    if (file) {
      const files = value.filter((f) => f !== file);
      onChange(id!, files);
    }
  };
  const handleDeleteAll = () => {
    onChange(id!, []);
  };

  return (
    <InputContainer
      id={id}
      description={description}
      label={label}
      error={error}
      isInvalid={isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <input
        id={id}
        type="file"
        ref={inputRef}
        hidden
        accept={acceptedTypes?.join(',')}
        {...rest}
        multiple
        onChange={handleInputChange}
      />
      <VStack alignItems="start" gap={3}>
        <Button variant="secondary" disabled={isDisabled} onClick={handleBrowse} type="button">
          Select File(s)
        </Button>
        {showFileNames === false ? (
          value.length !== 0 ? (
            <HStack gap={2} alignItems="center">
              <Text variant="body" color="text.primary" opacity={0.8}>
                {value.length} file(s) selected
              </Text>
              <IconButton
                aria-label="Delete all files"
                onClick={() => {
                  handleDeleteAll();
                }}
                variant="ghost"
                size="sm"
              >
                <RiCloseLine />
              </IconButton>
            </HStack>
          ) : null
        ) : (
          value.map((file) => (
            <HStack gap={2} alignItems="center" key={`${file.name}-${file.size}`}>
              <Text variant="body" color="text.primary" opacity={0.8}>
                {file && file.name}
              </Text>
              <IconButton
                aria-label="Delete file from list"
                onClick={() => {
                  handleDelete(file);
                }}
                variant="ghost"
                size="sm"
              >
                <RiCloseLine />
              </IconButton>
            </HStack>
          ))
        )}
      </VStack>
    </InputContainer>
  );
}
